import type { DxcBadgeVariant } from '../dxc.types';

/**
 * Formato → tono. Un solo mapa para todo el producto, por lo mismo que
 * `DXC_STATUS_VARIANT`: si cada pantalla escribe el suyo, el PDF acaba siendo
 * rojo en una y neutro en la de al lado. Lo usan la pastilla de
 * `<dxc-attachments>` y la ficha de formato de `.dxc-file-row`.
 */
export const DXC_FILE_FORMAT_VARIANT: Record<string, DxcBadgeVariant> = {
    pdf: 'danger', xls: 'success', xlsx: 'success', csv: 'success',
    doc: 'info', docx: 'info', xml: 'accent', txt: 'neutral', zip: 'neutral',
    jpg: 'warning', jpeg: 'warning', png: 'info', webp: 'accent', gif: 'accent',
};

export type DxcFileFormat = { label: string; tone: DxcBadgeVariant };

/**
 * Los tipos MIME cuyo subtipo no es la extensión del archivo. Sin esta tabla
 * la pastilla de un Excel decía `VND.OPENXMLFORMATS-OFFICEDOCUMENT.SPREADSHEETML.SHEET`
 * y, al no caber, se llevaba por delante el nombre del archivo.
 */
const MIME_SUBTYPE_FORMAT: Record<string, string> = {
    'vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
    'vnd.ms-excel': 'xls',
    'vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
    'msword': 'doc',
    'vnd.openxmlformats-officedocument.presentationml.presentation': 'pptx',
    'vnd.ms-powerpoint': 'ppt',
    'plain': 'txt',
    'svg+xml': 'svg',
    'x-icon': 'ico',
    'vnd.microsoft.icon': 'ico',
    'x-zip-compressed': 'zip',
};

/** Una extensión de verdad: de dos a cinco letras o cifras. */
const EXTENSION = /^[a-z0-9]{2,5}$/;

/**
 * El formato que dice `type`, que llega como tipo MIME (`application/pdf`) o
 * como extensión suelta (`pdf`, `.pdf`). Un subtipo que no es una extensión
 * (`octet-stream`, un `vnd.…` que no está en la tabla) no dice el formato:
 * devuelve vacío y decide la URL o el nombre.
 */
function formatOfType(type: string | null | undefined): string {
    if (!type) return '';

    const value = type.split(';')[0].trim().toLowerCase();
    const subtype = value.includes('/') ? value.split('/').pop()! : value.replace(/^\./, '');

    return MIME_SUBTYPE_FORMAT[subtype] ?? (EXTENSION.test(subtype) ? subtype : '');
}

/** La extensión al final de una ruta o de un nombre: de dos a cinco caracteres. */
function extensionOf(value: string | null | undefined): string {
    if (!value) return '';
    // De una URL solo cuenta la ruta: la consulta de un enlace firmado trae puntos.
    const path = value.split(/[?#]/)[0];
    const match = /\.([a-z0-9]{2,5})$/i.exec(path);
    return match ? match[1].toLowerCase() : '';
}

/**
 * El formato de un archivo, con su tono. Se deduce de lo primero que lo diga:
 * el tipo (MIME o extensión), la extensión de la URL y, por último, la del nombre — el nombre
 * que el usuario le dio a un documento a menudo no lleva extensión («Acta
 * constitutiva»), y la URL donde quedó guardado sí.
 *
 * Devuelve `null` cuando no se sabe: quien lo pinta enseña entonces el glifo
 * genérico en vez de inventarse un formato.
 */
export function dxcFileFormat(source: { type?: string | null; url?: string | null; name?: string | null }): DxcFileFormat | null {
    const extension = formatOfType(source.type) || extensionOf(source.url) || extensionOf(source.name);
    if (!extension) return null;
    return { label: extension.toUpperCase(), tone: DXC_FILE_FORMAT_VARIANT[extension] ?? 'neutral' };
}
